import { colors } from "@indeed/ifl-tokens/react-native";
import React, { type RefObject, useMemo } from "react";

import type { BoxRef } from "../../../../mads";
import { ChevronDown } from "../../../../mads/icons";
import { useI18n } from "../../../I18n";
import { asTranslatedString } from "../../../I18n/TranslatedString";
import { isWeb } from "../../../util/isWeb";
import { getPayTypesToLabels } from "../../../util/salary/getPayTypesToLabels";
import type { SalaryType } from "../../../util/salary/SalaryType";
import { ComboboxModal } from "../../ComboboxModal";
import { PickerCombobox } from "../../PickerCombobox";
import { Select } from "../../Select";
import { FormField } from "./FormField";

interface PayPeriodItem {
  /** GraphQL SalaryType value; widened to `string` to match form values. */
  value: string;
  label: string;
}

export interface PayPeriodDropdownProps {
  /** Pay periods to offer, in display order. */
  periods: readonly SalaryType[];
  /** Selected SalaryType value, or `""` for none. */
  value: string;
  onChange: (salaryType: string) => void;
  invalid?: boolean;
  errorText?: string;
  showRequiredIndicator?: boolean;
  /** Ref forwarded to the dropdown trigger for focus management (e.g. on a validation error). */
  triggerRef?: RefObject<BoxRef | null>;
  testID?: string;
  disabled?: boolean;
}

/**
 * Labeled pay-period dropdown shared by the pay editors. Native uses the picker sheet;
 * web uses Select.
 */
export const PayPeriodDropdown = ({
  periods,
  value,
  onChange,
  invalid,
  errorText,
  showRequiredIndicator = false,
  triggerRef,
  testID = "pay-period-select",
  disabled,
}: PayPeriodDropdownProps) => {
  const gt = useI18n();

  const payPeriodLabel = asTranslatedString(
    gt.pgettext("Pay period form field label", "Pay period"),
  );
  const placeholder = asTranslatedString(
    gt.pgettext("Pay period select placeholder", "Select pay period"),
  );

  const items = useMemo<PayPeriodItem[]>(() => {
    const payTypesToLabels = getPayTypesToLabels(gt);
    return periods.map((payType) => ({
      value: payType,
      label: `${payTypesToLabels[payType][0].toUpperCase()}${payTypesToLabels[payType].slice(1)}`,
    }));
  }, [gt, periods]);
  const selectedItem = items.find((item) => item.value === value);

  if (isWeb) {
    return (
      <Select
        value={selectedItem?.value}
        onChange={onChange}
        items={items}
        labelText={payPeriodLabel}
        placeholder={placeholder}
        required={showRequiredIndicator}
        requiredText={gt.gettext("Required")}
        invalid={!!invalid}
        errorText={errorText || undefined}
        disabled={disabled}
        buttonRef={triggerRef}
        testID={testID}
      />
    );
  }

  return (
    <FormField
      label={payPeriodLabel}
      showRequiredIndicator={showRequiredIndicator}
      invalid={!!invalid}
      errorText={errorText}
      disabled={disabled}
    >
      <PickerCombobox
        items={items}
        keyExtractor={(item) => item.value}
        labelExtractor={(item) => item.label}
        label={payPeriodLabel}
        modalTitle={payPeriodLabel}
        placeholderText={placeholder}
        // A string (not a <Text>) so Picker announces the label ("Per hour"), not the key ("HOURLY").
        renderItem={(item) => item.label}
        value={selectedItem}
        onSelect={(item) => onChange(item.value)}
        invalid={!!invalid}
        disabled={disabled}
        testID={testID}
        // Render the trigger here so `triggerRef` lands on the focusable button, not its wrapper.
        renderPseudoInput={({ handleOpenPopup }) => (
          <ComboboxModal.PseudoInput
            accessibilityLabel={payPeriodLabel}
            contentAfter={<ChevronDown aria-hidden color={colors.foregroundStrong} />}
            disabled={disabled}
            invalid={!!invalid}
            onPress={handleOpenPopup}
            placeholderText={placeholder}
            focusRef={triggerRef}
            testID={`${testID}-combobox-pseudo-input`}
            value={selectedItem?.label ?? ""}
          />
        )}
      />
    </FormField>
  );
};
